import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { CLOSURE_MESSAGE_MAX, type ArenaClosure, type ArenaClosureView } from '../../../shared/protocol.js';

type Flash = { ok: boolean; text: string } | null;
type Response = { closure: ArenaClosureView; presets: string[] };
type StartMode = 'now' | 'delay' | 'at';

const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const MINUTE = 60_000;

/** Epoch ms ↔ value of <input type="datetime-local"> in local time. */
function toLocalInput(ms: number): string {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
}
const fromLocalInput = (value: string): number | null => (value ? new Date(value).getTime() : null);
export const formatWhen = (ms: number) =>
  new Date(ms).toLocaleString('fi-FI', { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * Maintenance break (decision 2026-09-27): starts now (running games are aborted), after a
 * delay (no new games, running games continue with a countdown and are aborted when the break
 * begins) or at a given time (advance notice, new games blocked some minutes before).
 * Own message or editable preset messages.
 */
export function ClosureAdmin() {
  const [state, setState] = useState<ArenaClosureView | null>(null);
  const [presets, setPresets] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [startMode, setStartMode] = useState<StartMode>('delay');
  const [delayMin, setDelayMin] = useState(10);
  const [startAt, setStartAt] = useState('');
  const [drainMin, setDrainMin] = useState(10);
  const [endMode, setEndMode] = useState<'manual' | 'at'>('manual');
  const [endAt, setEndAt] = useState('');
  const [flash, setFlash] = useState<Flash>(null);
  const [confirm, setConfirm] = useState<{ closure: ArenaClosure; games: number } | null>(null);
  const [busy, setBusy] = useState(false);

  const apply = (r: Response) => {
    setState(r.closure);
    setPresets(r.presets);
    const c = r.closure;
    if (c.enabled && c.phase !== 'open') {
      setMessage(c.message);
      if (c.startsAt && c.startsAt > Date.now()) {
        setStartMode('at');
        setStartAt(toLocalInput(c.startsAt));
        setDrainMin(c.drainAt ? Math.round((c.startsAt - c.drainAt) / MINUTE) : 0);
      } else {
        setStartMode('now');
      }
      setEndMode(c.endsAt ? 'at' : 'manual');
      if (c.endsAt) setEndAt(toLocalInput(c.endsAt));
    }
  };

  const load = () =>
    api<Response>('GET', '/api/admin/closure')
      .then(apply)
      .catch((err) => setFlash({ ok: false, text: errorText(err) }));

  useEffect(() => {
    void load();
    // The phase changes on its own (scheduled start, automatic reopening).
    const timer = setInterval(
      () => void api<Response>('GET', '/api/admin/closure').then((r) => setState(r.closure)).catch(() => {}),
      15_000,
    );
    return () => clearInterval(timer);
  }, []);

  if (!state) return flash ? <p class="error">{flash.text}</p> : null;

  const save = async (closure: ArenaClosure, nextPresets = presets) => {
    setBusy(true);
    try {
      apply(await api<Response>('PUT', '/api/admin/closure', { ...closure, presets: nextPresets }));
      setFlash({ ok: true, text: t.admin.closure.saved });
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  const planned = (): ArenaClosure | null => {
    const text = message.trim();
    if (!text) {
      setFlash({ ok: false, text: t.admin.closure.messageRequired });
      return null;
    }
    const now = Date.now();
    let startsAt: number | null = null;
    let drainAt: number | null = null;
    if (startMode === 'delay') {
      startsAt = now + Math.max(1, delayMin) * MINUTE;
      drainAt = now;
    } else if (startMode === 'at') {
      startsAt = fromLocalInput(startAt);
      drainAt = startsAt === null ? null : Math.max(now, startsAt - Math.max(0, drainMin) * MINUTE);
    }
    const endsAt = endMode === 'at' ? fromLocalInput(endAt) : null;
    if ((startMode === 'at' && (!startsAt || startsAt <= now)) || (endMode === 'at' && (!endsAt || endsAt <= Math.max(now, startsAt ?? 0)))) {
      setFlash({ ok: false, text: t.admin.closure.badTimes });
      return null;
    }
    return { enabled: true, startsAt, drainAt, endsAt, message: text };
  };

  const submit = async (e: Event) => {
    e.preventDefault();
    const next = planned();
    if (!next) return;
    // Starting right now aborts the running games: confirm first.
    if (next.startsAt === null && !state.closed) {
      const games = await api<{ games: unknown[] }>('GET', '/api/admin/games')
        .then((r) => r.games.length)
        .catch(() => 0);
      setConfirm({ closure: next, games });
    } else {
      void save(next);
    }
  };

  const phase = state.phase;
  const status =
    phase === 'closed'
      ? t.admin.closure.stateClosed
      : phase === 'draining'
        ? t.admin.closure.stateDraining(formatWhen(state.startsAt!))
        : phase === 'upcoming'
          ? t.admin.closure.stateScheduled(formatWhen(state.startsAt!))
          : t.admin.closure.stateOpen;
  const active = phase !== 'open';
  const trimmed = message.trim();
  const radio = (mode: StartMode, label: string) => (
    <label class="radio">
      <input type="radio" name="closure-start" checked={startMode === mode} onChange={() => setStartMode(mode)} />
      {label}
    </label>
  );

  return (
    <>
      <form class="card form closure-admin" onSubmit={submit}>
        <h2>{t.admin.closure.title}</h2>
        <p class="muted">{t.admin.closure.info}</p>
        <p class={`closure-state ${phase}`} role="status">
          <span class="closure-dot" aria-hidden="true" />
          {status}
        </p>

        <label>
          {t.admin.closure.message}
          <textarea rows={3} value={message} maxLength={CLOSURE_MESSAGE_MAX} onInput={(e) => setMessage(e.currentTarget.value)} />
          <small>
            {trimmed.length}/{CLOSURE_MESSAGE_MAX}
          </small>
        </label>

        <div>
          <h3>{t.admin.closure.presets}</h3>
          <ul class="preset-list">
            {presets.map((p, i) => (
              <li class="preset-row">
                <button type="button" class="btn btn-small" onClick={() => setMessage(p)}>
                  {t.admin.closure.usePreset}
                </button>
                <span class="preset-text">{p}</span>
                <button
                  type="button"
                  class="btn btn-ghost btn-small"
                  title={t.admin.closure.removePreset}
                  aria-label={t.admin.closure.removePreset}
                  disabled={busy}
                  onClick={() => void save(state, presets.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            class="btn btn-small"
            disabled={busy || !trimmed || presets.includes(trimmed) || presets.length >= 12}
            onClick={() => void save(state, [...presets, trimmed])}
          >
            ＋ {t.admin.closure.addPreset}
          </button>
        </div>

        <div class="grid-2">
          <fieldset class="closure-when">
            <legend>{t.admin.closure.start}</legend>
            {radio('now', t.admin.closure.startNow)}
            {startMode === 'now' && <small class="closure-hint">{t.admin.closure.startNowHint}</small>}
            {radio('delay', t.admin.closure.startDelay)}
            {startMode === 'delay' && (
              <>
                <span class="closure-inline">
                  <input
                    type="number"
                    min={1}
                    max={240}
                    value={delayMin}
                    onInput={(e) => setDelayMin(Number(e.currentTarget.value))}
                    aria-label={t.admin.closure.delayMinutes}
                    required
                  />
                  {t.admin.closure.delayMinutes}
                </span>
                <small class="closure-hint">{t.admin.closure.startDelayHint}</small>
              </>
            )}
            {radio('at', t.admin.closure.startAt)}
            {startMode === 'at' && (
              <>
                <input type="datetime-local" value={startAt} onInput={(e) => setStartAt(e.currentTarget.value)} required />
                <span class="closure-inline">
                  <input
                    type="number"
                    min={0}
                    max={240}
                    value={drainMin}
                    onInput={(e) => setDrainMin(Number(e.currentTarget.value))}
                    aria-label={t.admin.closure.drainBefore}
                  />
                  {t.admin.closure.drainBefore}
                </span>
                <small class="closure-hint">{t.admin.closure.startAtHint}</small>
              </>
            )}
          </fieldset>
          <fieldset class="closure-when">
            <legend>{t.admin.closure.end}</legend>
            <label class="radio">
              <input type="radio" name="closure-end" checked={endMode === 'manual'} onChange={() => setEndMode('manual')} />
              {t.admin.closure.endManual}
            </label>
            <label class="radio">
              <input type="radio" name="closure-end" checked={endMode === 'at'} onChange={() => setEndMode('at')} />
              {t.admin.closure.endAt}
            </label>
            {endMode === 'at' && <input type="datetime-local" value={endAt} onInput={(e) => setEndAt(e.currentTarget.value)} required />}
          </fieldset>
        </div>

        {flash && (
          <p class={flash.ok ? 'success' : 'error'} role="status">
            {flash.text}
          </p>
        )}
        <div class="closure-actions">
          <button class="btn btn-danger" type="submit" disabled={busy}>
            {active ? t.admin.closure.update : startMode === 'now' ? t.admin.closure.closeNow : t.admin.closure.schedule}
          </button>
          {active && (
            <button
              class="btn btn-primary"
              type="button"
              disabled={busy}
              onClick={() => void save({ enabled: false, startsAt: null, drainAt: null, endsAt: null, message: state.message })}
            >
              {t.admin.closure.open}
            </button>
          )}
        </div>
      </form>
      {confirm && (
        <ConfirmDialog
          title={t.admin.closure.confirmTitle}
          confirmLabel={t.admin.closure.closeNow}
          cancelLabel={t.admin.cancel}
          danger
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            const next = confirm.closure;
            setConfirm(null);
            void save(next);
          }}
        >
          <p>{t.admin.closure.confirmText(confirm.games)}</p>
        </ConfirmDialog>
      )}
    </>
  );
}
